import { OE_TYPE_LABEL_KEYS } from '../../unit-picker-options';
import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  resource,
  signal,
} from '@angular/core';
import { isBrowser } from '@mb/common/client';
import type { PersonSearchDto } from '@modules/contacts/shared';
import {
  FormField,
  FormRoot,
  form,
  maxLength,
  pattern,
  required,
} from '@angular/forms/signals';
import { TranslocoPipe } from '@jsverse/transloco';
import { ISO_COUNTRY_CODES, ISO_COUNTRY_LABELS } from '@mb/common';
import { HlmButtonImports } from '@mb/ui/button';
import { HlmButtonGroupImports } from '@mb/ui/button-group';
import { HlmFieldImports } from '@mb/ui/field';
import { OrgRolePickerComponent } from './org-role-picker.component';
import { syncPositionRoles } from './position-roles';
import { HlmInputImports } from '@mb/ui/input';
import {
  allowedChildOeTypes,
  canHoldConfiguration,
  ORGANIZATION_UNIT_TYPES,
  type OrganizationUnitType,
} from '../../../shared';
import {
  OrgChartApiService,
  type OrgRoleOption,
} from '../../services/org-chart-api.service';
import {
  DetailsShellComponent,
  SearchSelectComponent,
  type SearchSelectOption,
} from '@mb/components';
import { saveErrorKey } from './placement-error';
import {
  ORG_EDIT_SHEET_SECTIONS,
  type OrgEditPostKind,
} from './org-edit-sheet-sections';
import { OrgChartStore } from './org-chart.state';
import type { OrgOePanelRequest } from './org-chart.types';

interface OrgEditModel {
  name: string;
  type: OrganizationUnitType;
  legalName: string;
  countryCode: string;
  /** IANA zone; empty inherits from the parent OE. */
  timezone: string;
  /**
   * Holiday entitlement, both empty-inherits. Held as strings because that is
   * what a select and a number input hand back; the edge turns them into a
   * number and a null.
   */
  vacationWaitingPeriodMonths: string;
  vacationYearType: string;
  description: string;
  /** Id of the person answering for a station; empty means nobody yet. */
  responsiblePersonId: string;
}

type VacationYearType = 'WORK_YEAR' | 'CALENDAR_YEAR';

const VACATION_YEAR_TYPE_OPTIONS: {
  value: VacationYearType;
  labelKey: string;
}[] = [
  { value: 'WORK_YEAR', labelKey: 'organigram.panel.vacationYear.workYear' },
  {
    value: 'CALENDAR_YEAR',
    labelKey: 'organigram.panel.vacationYear.calendarYear',
  },
];

/**
 * The holiday year as the API wants it, or null for "inherit".
 *
 * Narrowed against the options rather than cast: the form holds a string, and
 * a value the select never offered — a stale record, a hand-edited payload —
 * would otherwise be sent on as though it were one of the two, to be refused
 * by the server with a message about a field the user cannot see.
 */
function yearTypeOrNull(text: string): VacationYearType | null {
  const trimmed = text.trim();
  const known = VACATION_YEAR_TYPE_OPTIONS.find(
    (option) => option.value === trimmed,
  );
  return known?.value ?? null;
}

/**
 * A waiting period as the API wants it: a number, or null for "inherit".
 *
 * Written out rather than `Number(text) || null`, because `0` is a value the
 * customer's field list names explicitly — "0 = sofort" — and `||` would turn
 * it back into "nothing set here".
 */
function monthsOrNull(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const months = Number(trimmed);
  return Number.isInteger(months) && months >= 0 ? months : null;
}

/**
 * Every type, with its label, in the order of the tree.
 *
 * The list the select is built from; which of them a given parent may take
 * is `allowedChildOeTypes`'s business, not this one's. Built from
 * `ORGANIZATION_UNIT_TYPES` rather than written out, so a type added to the
 * language cannot be left out of the dropdown — which is exactly what
 * happened to the profit centre for one test run: the rule offered it, the
 * select had never heard of it, and the field went empty.
 */
const TYPE_OPTIONS: { value: OrganizationUnitType; labelKey: string }[] =
  ORGANIZATION_UNIT_TYPES.map((value) => ({
    value,
    labelKey: OE_TYPE_LABEL_KEYS[value],
  }));

const EMPTY_MODEL: OrgEditModel = {
  name: '',
  type: 'department',
  legalName: '',
  countryCode: '',
  timezone: '',
  vacationWaitingPeriodMonths: '',
  vacationYearType: '',
  description: '',
  responsiblePersonId: '',
};

/** Creates or edits an organization unit, position or station in the org-chart sheet. */
@Component({
  selector: 'org-chart-oe-edit-panel',
  standalone: true,
  imports: [
    FormField,
    FormRoot,
    HlmFieldImports,
    HlmInputImports,
    HlmButtonImports,
    HlmButtonGroupImports,
    DetailsShellComponent,
    SearchSelectComponent,
    TranslocoPipe,
    NgComponentOutlet,
    OrgRolePickerComponent,
  ],
  template: `
    <app-details-shell [bare]="embedded()">
      @if (!embedded()) {
        <h2 detailsTrail class="truncate font-medium text-foreground">
          {{ titleKey() | transloco }}
        </h2>
      }

      <hlm-button-group detailsActions>
        @if (canDelete()) {
          <button
            type="button"
            hlmBtn
            variant="destructive"
            [disabled]="saving()"
            (click)="delete()"
          >
            {{ 'organigram.panel.actions.delete' | transloco }}
          </button>
        }
        <button
          type="button"
          hlmBtn
          variant="outline"
          [disabled]="saving()"
          (click)="cancelled.emit()"
        >
          {{ 'organigram.panel.actions.cancel' | transloco }}
        </button>
        <button
          type="button"
          hlmBtn
          [disabled]="saving() || formTree().invalid()"
          (click)="save()"
        >
          {{
            (saving()
              ? 'organigram.panel.actions.saving'
              : 'organigram.panel.actions.save'
            ) | transloco
          }}
        </button>
      </hlm-button-group>

      <!--
        The same surface every other configuration form sits on. Without it
        this one was the only white form among grey ones, which reads as a
        different kind of thing rather than the same thing about a different
        subject. On the form itself rather than a wrapper around it, so the
        fields keep the nesting they had.
      -->
      <form
        [formRoot]="formTree"
        class="rounded-md border border-border bg-form-surface p-3"
      >
        <div hlmFieldGroup>
          <div
            hlmField
            [attr.data-invalid]="
              formTree.name().touched() && formTree.name().invalid()
            "
          >
            <label hlmFieldLabel for="oc-edit-name">{{
              'organigram.panel.fields.name' | transloco
            }}</label>
            <input
              id="oc-edit-name"
              hlmInput
              type="text"
              [formField]="formTree.name"
              [attr.aria-invalid]="
                formTree.name().touched() && formTree.name().invalid()
              "
            />
            @if (formTree.name().touched() && formTree.name().invalid()) {
              @for (err of formTree.name().errors(); track $index) {
                <hlm-field-error [forceShow]="true">{{
                  err.message | transloco
                }}</hlm-field-error>
              }
            }
          </div>

          @if (request().kind === 'orgUnit') {
            <div hlmField>
              <label hlmFieldLabel for="oc-edit-type">{{
                'organigram.panel.fields.type' | transloco
              }}</label>
              <select id="oc-edit-type" hlmInput [formField]="formTree.type">
                @for (option of typeOptions(); track option.value) {
                  <option [value]="option.value">
                    {{ option.labelKey | transloco }}
                  </option>
                }
              </select>
            </div>

            <div hlmField>
              <label hlmFieldLabel for="oc-edit-oe-desc">{{
                'organigram.panel.fields.description' | transloco
              }}</label>
              <textarea
                id="oc-edit-oe-desc"
                hlmInput
                rows="3"
                [formField]="formTree.description"
              ></textarea>
            </div>

            <!-- A legal identifier belongs to a legal entity, nothing else. -->
            @if (keepsLegalName()) {
              <div hlmField>
                <label hlmFieldLabel for="oc-edit-legal">{{
                  'organigram.panel.fields.legalName' | transloco
                }}</label>
                <input
                  id="oc-edit-legal"
                  hlmInput
                  type="text"
                  [formField]="formTree.legalName"
                />
              </div>
            }

            <!--
              Country, timezone and core time only where the unit keeps its
              own. A department inherits all three from the company, so the
              fields would offer a choice it does not have.
            -->
            @if (keepsOwnSettings()) {
              <div hlmField>
                <label hlmFieldLabel for="oc-edit-country">{{
                  'organigram.panel.fields.countryCode' | transloco
                }}</label>
                <select
                  id="oc-edit-country"
                  hlmInput
                  [formField]="formTree.countryCode"
                >
                  <option value="">
                    {{ 'organigram.panel.fields.countryCodeNone' | transloco }}
                  </option>
                  @for (code of countryCodes; track code) {
                    <option [value]="code">
                      {{ code }} — {{ countryLabels[code] }}
                    </option>
                  }
                </select>
              </div>

              <div hlmField>
                <label hlmFieldLabel for="oc-edit-timezone">{{
                  'organigram.panel.fields.timezone' | transloco
                }}</label>
                <app-search-select
                  inputId="oc-edit-timezone"
                  testId="oc-edit-timezone"
                  [options]="timeZoneOptions"
                  [value]="formTree.timezone().value() || null"
                  [placeholder]="
                    'organigram.panel.fields.timezoneInherited' | transloco
                  "
                  [emptyText]="'common.noMatches' | transloco"
                  [clearLabel]="
                    'organigram.panel.fields.timezoneInherited' | transloco
                  "
                  (valueChange)="formTree.timezone().value.set($event ?? '')"
                />
                <p class="text-xs text-muted-foreground">
                  {{ 'organigram.panel.fields.timezoneHint' | transloco }}
                </p>
              </div>

              <!--
              The two holiday parameters, inherited the same way. Left empty
              they take what the unit above names; the waiting period accepts
              0, which is a decision ("from the first day") and not an empty
              field, so the placeholder has to say "inherited" rather than 0.
            -->
              <div hlmField>
                <label hlmFieldLabel for="oc-edit-vacation-year">{{
                  'organigram.panel.fields.vacationYearType' | transloco
                }}</label>
                <select
                  id="oc-edit-vacation-year"
                  hlmInput
                  [formField]="formTree.vacationYearType"
                >
                  <option value="">
                    {{
                      'organigram.panel.fields.vacationInherited' | transloco
                    }}
                  </option>
                  @for (option of vacationYearTypes; track option.value) {
                    <option [value]="option.value">
                      {{ option.labelKey | transloco }}
                    </option>
                  }
                </select>
              </div>

              <div hlmField>
                <label hlmFieldLabel for="oc-edit-vacation-wait">{{
                  'organigram.panel.fields.vacationWaitingPeriodMonths'
                    | transloco
                }}</label>
                <input
                  id="oc-edit-vacation-wait"
                  hlmInput
                  type="number"
                  [formField]="formTree.vacationWaitingPeriodMonths"
                />
                @if (
                  formTree.vacationWaitingPeriodMonths().touched() &&
                  formTree.vacationWaitingPeriodMonths().invalid()
                ) {
                  @for (
                    err of formTree.vacationWaitingPeriodMonths().errors();
                    track $index
                  ) {
                    <p class="text-xs text-destructive">
                      {{ err.message ?? '' | transloco }}
                    </p>
                  }
                }
                <p class="text-xs text-muted-foreground">
                  {{
                    'organigram.panel.fields.vacationWaitingPeriodHint'
                      | transloco
                  }}
                </p>
              </div>
            }
          } @else if (request().kind === 'station') {
            <div hlmField>
              <label hlmFieldLabel for="oc-edit-station-desc">{{
                'organigram.panel.fields.description' | transloco
              }}</label>
              <textarea
                id="oc-edit-station-desc"
                hlmInput
                rows="3"
                [formField]="formTree.description"
              ></textarea>
            </div>

            <!--
              Who answers for the station — any person of the organization,
              not only of the unit it sits on.
            -->
            <div hlmField>
              <label hlmFieldLabel for="oc-edit-responsible">{{
                'organigram.panel.fields.responsiblePerson' | transloco
              }}</label>
              <input
                id="oc-edit-responsible"
                hlmInput
                type="search"
                autocomplete="off"
                [placeholder]="
                  'organigram.panel.fields.responsiblePersonSearch' | transloco
                "
                [value]="personSearch()"
                (input)="onPersonSearch($event)"
              />
              @if (chosenPersonName(); as name) {
                <p class="text-xs text-muted-foreground">
                  {{
                    'organigram.panel.fields.responsiblePersonChosen'
                      | transloco
                  }}
                  <strong>{{ name }}</strong>
                  <button
                    type="button"
                    class="ms-2 underline"
                    (click)="clearPerson()"
                  >
                    {{
                      'organigram.panel.fields.responsiblePersonClear'
                        | transloco
                    }}
                  </button>
                </p>
              }
              @if (personMatches().length) {
                <!--
                  Truncated per row, not merely capped in height: a name
                  like "Musterbauer Johanna-Friederike" has nothing to wrap
                  on, and without it the longest match decides how wide the
                  sheet is. Seen in a browser at 320px, where it pushed the
                  form off its own card.
                -->
                <ul
                  class="mt-1 max-h-40 overflow-y-auto rounded-md border border-border"
                >
                  @for (person of personMatches(); track person.id) {
                    <li>
                      <button
                        type="button"
                        class="block w-full truncate px-2 py-1 text-start text-sm hover:bg-muted"
                        (click)="choosePerson(person)"
                      >
                        {{ person.lastName }} {{ person.firstName }}
                      </button>
                    </li>
                  }
                </ul>
              } @else if (personSearch().trim().length > 0) {
                <p class="text-xs text-muted-foreground">
                  {{
                    'organigram.panel.fields.responsiblePersonNone' | transloco
                  }}
                </p>
              }
            </div>
          } @else {
            <div hlmField>
              <label hlmFieldLabel for="oc-edit-desc">{{
                'organigram.panel.fields.description' | transloco
              }}</label>
              <textarea
                id="oc-edit-desc"
                hlmInput
                rows="4"
                [formField]="formTree.description"
              ></textarea>
            </div>

            <div hlmField>
              <label hlmFieldLabel>{{
                'organigram.panel.fields.roles' | transloco
              }}</label>
              <org-role-picker
                [roles]="roles()"
                [(selected)]="selectedRoleIds"
              />
            </div>
          }
        </div>

        @if (error(); as err) {
          <hlm-field-error [forceShow]="true">{{
            err | transloco
          }}</hlm-field-error>
        }
      </form>

      <!--
        Outside the form on purpose: a contributed section is a list that
        saves each change by itself, not fields the save button commits.
        Shown only once the post exists, because those changes need
        something to hang on.
      -->
      @if (post(); as current) {
        @for (section of sectionsFor(current.kind); track section.id) {
          <div class="mt-6 border-t border-border pt-6">
            <ng-container
              *ngComponentOutlet="
                section.component;
                inputs: {
                  owner: current.kind,
                  ownerId: current.id,
                  organizationId: organizationId(),
                  canEdit: true,
                }
              "
            />
          </div>
        }
      }
    </app-details-shell>
  `,
})
export class OrgChartOeEditPanelComponent {
  readonly #api = inject(OrgChartApiService);
  readonly #store = inject(OrgChartStore);
  readonly countryCodes = ISO_COUNTRY_CODES;
  readonly vacationYearTypes = VACATION_YEAR_TYPE_OPTIONS;
  readonly countryLabels = ISO_COUNTRY_LABELS;
  /**
   * Zones offered in the dropdown.
   *
   * Read from the runtime rather than hard-coded, so the list cannot drift from
   * what the server accepts — both sides resolve names through the same `Intl`
   * database. Falls back to a short European list where the browser does not
   * expose the enumeration yet.
   */
  readonly timeZones: readonly string[] = (
    Intl as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf?.('timeZone') ?? [
    'Europe/Vienna',
    'Europe/Berlin',
    'Europe/Zurich',
    'Europe/Rome',
    'Europe/Lisbon',
    'UTC',
  ];

  /** Some four hundred of them, so typed into rather than scrolled (UI-018). */
  readonly timeZoneOptions: readonly SearchSelectOption[] = this.timeZones.map(
    (zone) => ({ id: zone, label: zone }),
  );

  readonly request = input.required<OrgOePanelRequest>();
  /**
   * Rendered inside the configuration panel rather than as a sheet of its own.
   *
   * Only the chrome differs — the form, its validation and its save path are
   * the same in both places, which is the point: the unit is edited by one
   * piece of code however it was reached.
   */
  readonly embedded = input(false);
  readonly roleOptions = input<OrgRoleOption[]>([]);

  readonly saved = output<void>();
  readonly cancelled = output<void>();

  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Whether this unit keeps a country, a timezone and a core time of its own.
   *
   * The same rule that decides whether it holds configuration — these three
   * are configuration, just on the record rather than beside it. A department
   * takes all of them from the company above.
   *
   * Read from the type the form currently holds, not the one it was opened
   * with: changing the type in this very form has to show or hide them at
   * once, or the fields would describe a unit that no longer exists.
   */
  readonly keepsOwnSettings = computed(() =>
    canHoldConfiguration(this.formTree().value().type),
  );

  /** The catalogue to offer; the tree already knows which organization it is. */
  readonly organizationId = computed(
    () => this.#store.organizationId() || null,
  );

  readonly #sections = inject(ORG_EDIT_SHEET_SECTIONS);

  /**
   * Which post, if any, the contributed sections apply to here: a Stelle or
   * a station, never an OE. Creating is excluded because the post has no id
   * yet.
   */
  readonly post = computed((): { kind: OrgEditPostKind; id: string } | null => {
    const request = this.request();
    if (request.mode !== 'edit' || request.kind === 'orgUnit') return null;
    return { kind: request.kind, id: request.id };
  });

  sectionsFor(kind: OrgEditPostKind) {
    return this.#sections.filter((section) => section.appliesTo.includes(kind));
  }

  /**
   * A legal identifier belongs to a legal entity.
   *
   * The field was shown on every unit, which on a department is an
   * invitation to fill in something that means nothing. A profit centre
   * configures like a company but is not one either.
   */
  readonly keepsLegalName = computed(
    () => this.formTree().value().type === 'company',
  );

  /**
   * Looking for the person who answers for a station.
   *
   * Asked of the server on every keystroke rather than filtered in the
   * browser: the customer's words were that a larger company has very, very
   * many employees, and a list of all of them is both a slow page and a
   * scroll nobody finishes. The resource re-runs when the term changes and
   * cancels the request it replaces.
   */
  readonly personSearch = signal('');

  readonly #personsResource = resource({
    params: () => {
      const term = this.personSearch().trim();
      // From the store, not the request: an edit request carries the unit,
      // not the organization it sits in.
      const organizationId = this.#store.organizationId();
      if (!isBrowser() || !organizationId || term.length < 2) return undefined;
      return { organizationId, search: term };
    },
    loader: async ({ params, abortSignal }) => {
      try {
        return await this.#api.listSearchPersons(params, {
          signal: abortSignal,
        });
      } catch {
        return [] as PersonSearchDto[];
      }
    },
  });

  readonly personMatches = computed((): readonly PersonSearchDto[] =>
    this.#personsResource.hasValue() ? this.#personsResource.value() : [],
  );

  /**
   * The name of whoever the form currently names, for showing.
   *
   * Carried rather than looked up: the search only knows who matched the
   * last term, and a station opened with somebody responsible has to show
   * their name before anybody types anything. It comes with the tree for
   * that reason.
   */
  readonly chosenPersonName = linkedSignal<string>(() => {
    const request = this.request();
    return request.mode === 'edit' && request.kind === 'station'
      ? request.initial.responsiblePersonName
      : '';
  });

  onPersonSearch(event: Event): void {
    this.personSearch.set((event.target as HTMLInputElement).value);
  }

  choosePerson(person: PersonSearchDto): void {
    this.chosenPersonName.set(`${person.lastName} ${person.firstName}`.trim());
    this.#model.update((model) => ({
      ...model,
      responsiblePersonId: person.id,
    }));
    this.personSearch.set('');
  }

  clearPerson(): void {
    this.chosenPersonName.set('');
    this.#model.update((model) => ({ ...model, responsiblePersonId: '' }));
  }

  readonly canDelete = computed(() => {
    const request = this.request();
    return request.mode === 'edit' && request.canDelete;
  });

  /**
   * The types this unit may be given, asked of the same rule the server
   * enforces. Offering one the server would refuse turns a rule into a failed
   * save, which is the worst of both.
   */
  readonly typeOptions = computed(() => {
    const request = this.request();
    // The selector is only rendered for an organization unit.
    if (request.kind !== 'orgUnit') return [];
    // Root stays root, and nothing else is ever promoted to organization.
    if (request.mode === 'edit' && request.initial.type === 'organization') {
      return TYPE_OPTIONS.filter((option) => option.value === 'organization');
    }
    const allowed = allowedChildOeTypes(request.parent);
    /*
      A unit already saved as a company keeps company on the list even where
      the rule would no longer offer it. Dropping it would leave the select
      with nothing matching the model, showing one type while the record holds
      another — and writing the shown one back on the next save.
    */
    const saved = request.mode === 'edit' ? request.initial.type : null;
    return TYPE_OPTIONS.filter(
      (option) => allowed.includes(option.value) || option.value === saved,
    );
  });

  readonly roles = computed(() =>
    this.request().kind === 'position' ? this.roleOptions() : [],
  );

  readonly selectedRoleIds = linkedSignal<string[]>(() => {
    const request = this.request();
    return request.mode === 'edit' && request.kind === 'position'
      ? [...request.initial.roleIds]
      : [];
  });

  readonly titleKey = computed(() => {
    const request = this.request();
    if (request.kind === 'orgUnit') {
      return request.mode === 'create'
        ? 'organigram.panel.title.createOe'
        : 'organigram.panel.title.editOe';
    }
    if (request.kind === 'station') {
      return request.mode === 'create'
        ? 'organigram.panel.title.createStation'
        : 'organigram.panel.title.editStation';
    }
    return request.mode === 'create'
      ? 'organigram.panel.title.createPosition'
      : 'organigram.panel.title.editPosition';
  });

  /**
   * The type a new unit starts on.
   *
   * A department, as before, wherever one is allowed — that is what most new
   * units are. Under the root it is the one type that is not, and leaving the
   * default there would show "Firma" in the select while the model still said
   * department, so the save would be refused by the very rule the form had
   * just applied.
   */
  #defaultCreateType(): OrganizationUnitType {
    const offered = this.typeOptions().map((option) => option.value);
    if (offered.includes(EMPTY_MODEL.type)) return EMPTY_MODEL.type;
    return offered[0] ?? EMPTY_MODEL.type;
  }

  readonly #model = linkedSignal<OrgEditModel>(() => {
    const request = this.request();
    return request.mode === 'edit'
      ? {
          name: request.initial.name,
          type: request.initial.type,
          legalName: request.initial.legalName,
          countryCode: request.initial.countryCode,
          timezone: request.initial.timezone,
          vacationWaitingPeriodMonths:
            request.initial.vacationWaitingPeriodMonths,
          vacationYearType: request.initial.vacationYearType,
          description: request.initial.description,
          responsiblePersonId: request.initial.responsiblePersonId,
        }
      : { ...EMPTY_MODEL, type: this.#defaultCreateType() };
  });

  readonly formTree = form(this.#model, (schema) => {
    required(schema.name, {
      message: 'organigram.panel.validation.nameRequired',
    });
    maxLength(schema.name, 255, {
      message: 'organigram.panel.validation.nameMaxLength',
    });
    maxLength(schema.legalName, 255, {
      message: 'organigram.panel.validation.legalNameMaxLength',
    });
    maxLength(schema.description, 1000, {
      message: 'organigram.panel.validation.descriptionMaxLength',
    });
    /*
      A whole number of months from 0 to 120. Expressed here rather than as
      `min`/`max` on the input, which Signal Forms refuses on a node it binds —
      and a regex is what the field actually holds, a string.

      No `^$` alternative: `pattern` does not apply to an empty field, which is
      how "inherit from the unit above" stays allowed. Written out because the
      alternative looked like it was doing that work and was not.
    */
    pattern(schema.vacationWaitingPeriodMonths, /^(0|[1-9]\d?|1[01]\d|120)$/, {
      message: 'organigram.panel.validation.vacationWaitingPeriodRange',
    });
  });

  delete(): void {
    const request = this.request();
    if (request.mode !== 'edit' || !request.canDelete) return;
    this.#store.remove({ kind: request.kind, id: request.id });
  }

  async save(): Promise<void> {
    if (this.formTree().invalid()) return;

    this.saving.set(true);
    this.error.set(null);
    try {
      await this.#submit(this.request());
      this.saved.emit();
    } catch (error) {
      this.error.set(saveErrorKey(error));
    } finally {
      this.saving.set(false);
    }
  }

  async #submit(request: OrgOePanelRequest): Promise<void> {
    const value = this.#model();
    const name = value.name.trim();

    if (request.mode === 'edit') {
      if (request.kind === 'orgUnit') {
        await this.#api.updateUnit({
          id: request.id,
          name,
          type: value.type,
          description: value.description.trim() || null,
          legalName: value.legalName.trim() || null,
          countryCode: value.countryCode.trim() || null,
          // Empty means "inherit", which is null in the column.
          timezone: value.timezone.trim() || null,
          // `|| null` would swallow a waiting period of 0, which is a
          // decision ("from the first day") and not an empty field.
          vacationWaitingPeriodMonths: monthsOrNull(
            value.vacationWaitingPeriodMonths,
          ),
          vacationYearType: yearTypeOrNull(value.vacationYearType),
        });
      } else if (request.kind === 'station') {
        await this.#api.updateStation({
          id: request.id,
          name,
          description: value.description.trim() || null,
          responsiblePersonId: value.responsiblePersonId || null,
        });
      } else {
        await this.#api.updatePosition({
          id: request.id,
          name,
          description: value.description.trim() || null,
        });
        await this.#syncRoles(request.id, request.initial.roleIds);
      }
      return;
    }

    if (request.kind === 'orgUnit') {
      await this.#api.createUnit({
        name,
        type: value.type,
        organizationId: request.organizationId,
        parentId: request.parentOeId,
        legalName: value.legalName.trim() || undefined,
        countryCode: value.countryCode.trim() || undefined,
        timezone: value.timezone.trim() || undefined,
        vacationWaitingPeriodMonths:
          monthsOrNull(value.vacationWaitingPeriodMonths) ?? undefined,
        vacationYearType: yearTypeOrNull(value.vacationYearType) ?? undefined,
      });
    } else if (request.kind === 'station') {
      await this.#api.createStation({
        name,
        organizationUnitId: request.organizationUnitId,
        description: value.description.trim() || undefined,
        responsiblePersonId: value.responsiblePersonId || undefined,
      });
    } else {
      const created = await this.#api.createPosition({
        name,
        organizationUnitId: request.organizationUnitId,
        description: value.description.trim() || undefined,
      });
      await this.#syncRoles(created.id, []);
    }
  }

  #syncRoles(positionId: string, before: readonly string[]): Promise<void> {
    return syncPositionRoles(this.#api, positionId, before, this.selectedRoleIds());
  }
}
